import { Box, Typography } from "@mui/material";
import { FieldStatus, FieldStatusBadge } from "./FieldStatusBadge";
import {
  IPrequalificationFieldError,
  IPrequalificationInfoResponse,
  IPrequalifyData,
  PrequalificationFormType,
  PrequalificationSchema,
} from "../../../../services/prequalification";
import {
  IScratchDisclosure,
  PREQUAL_COPY,
  SCRATCH_DISCLOSURES,
  ScratchDisclosureKey,
} from "../constants";
import React, { useEffect, useMemo, useRef } from "react";
import { US_STATES, normalizeUsState } from "../../../../config/usStates";
import { getPhonePattern, openExternalLink } from "../../../../utils";

import AvatarCustom from "../../../../components/AvatarCustom/AvatarCustom";
// Controllers are imported directly (not via the Controllers barrel): the
// barrel transitively drags in the whole components/modals tree, which pulls
// firebase into any jest suite that renders this form.
import GvCheckboxController from "../../../../components/Controllers/GvCheckboxController/GvCheckboxController";
import GvDatePickerController from "../../../../components/Controllers/GvDatePickerController/GvDatePickerController";
import GvInputController from "../../../../components/Controllers/GvInputController/GvInputController";
import GvInputMaskController from "../../../../components/Controllers/GvInputMaskController/GvInputMaskController";
import GvPrimaryButton from "../../../../components/GvPrimaryButton/GvPrimaryButton";
import { IPetItemResponse } from "../../../../services/pet-list/pet-list.interface";
import { IUserItemResponse } from "../../../../services/user-detail/user-detail.interface";
import { MIXPANEL_EVENTS } from "../../../../config/mixPanelEvents";
import {
  PrequalFormFieldName,
  SERVER_ERROR_FIELD_MAP,
  getEditableFormFields,
  getFormFieldErrors,
} from "../serverFieldErrors";
import PrequalStateSelect from "./PrequalStateSelect";
import { format, parse } from "date-fns";
import { logPrequalEvent } from "../prequalAnalytics";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

interface IPrequalCustomerInfoFormProps {
  pet?: IPetItemResponse;
  prefill?: IPrequalificationInfoResponse;
  user?: IUserItemResponse;
  /**
   * The previously submitted payload. The form unmounts during processing,
   * so this restores what the customer entered when they are sent back.
   */
  initialValues?: Omit<IPrequalifyData, "petId"> | null;
  /**
   * Field errors from a failed submit. Messages for keys that map to a form
   * field are displayed verbatim; other keys are ignored.
   */
  serverErrors?: IPrequalificationFieldError[] | null;
  onSubmit: (data: Omit<IPrequalifyData, "petId">) => void;
}

const buildSubmittedValues = (submitted: Omit<IPrequalifyData, "petId">) => ({
  firstName: submitted.firstName,
  lastName: submitted.lastName,
  email: submitted.email,
  phone: getPhonePattern(submitted.phone),
  street: submitted.street,
  city: submitted.city,
  state: submitted.state,
  zip: submitted.zip,
  dateOfBirth: parse(submitted.dateOfBirth, "yyyy-MM-dd", new Date()),
  isAgreed: true,
});

type PrefillValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  zip: string;
};

// The user-store fallback keeps city and state in one free-text townCity
// field ("Chicago, IL"); the backend prefill already splits it the same way.
const splitTownCity = (townCity?: string): { city: string; state: string } => {
  if (!townCity) return { city: "", state: "" };
  const parts = townCity.split(",");
  if (parts.length > 1) {
    const stateCandidate = normalizeUsState(parts[parts.length - 1]);
    if (stateCandidate) {
      return { city: parts.slice(0, -1).join(",").trim(), state: stateCandidate };
    }
  }
  return { city: townCity.trim(), state: "" };
};

const buildPrefillValues = (
  prefill?: IPrequalificationInfoResponse,
  user?: IUserItemResponse,
): PrefillValues => {
  const fallback = splitTownCity(user?.addressInfo?.townCity);
  const phone = prefill?.phone || user?.phone || "";

  return {
    firstName: prefill?.firstName || user?.firstName || "",
    lastName: prefill?.lastName || user?.lastName || "",
    email: prefill?.email || user?.email || "",
    phone: phone ? getPhonePattern(phone) : "",
    street: prefill?.street || user?.addressInfo?.streetAddress || "",
    city: prefill?.city || fallback.city,
    state: normalizeUsState(prefill?.state) || fallback.state,
    zip: prefill?.zip || user?.addressInfo?.zipCode || "",
  };
};

// GvInputMask carries its own 5px top margin, 13px/43px sizing and a tinted
// error state — align it with the 48px/14px Figma inputs and the FIELD_SX
// error border so phone matches its siblings.
const MASKED_FIELD_SX = {
  "& input": {
    marginTop: 0,
    minHeight: "3rem",
    fontSize: "0.875rem",
  },
  "& input.error": {
    borderColor: "#ED7857",
    backgroundColor: "#FFFFFF",
  },
} as const;

const FIELD_SX = {
  // The theme gives every FormControl marginBottom: 1rem; the form's own
  // gap already provides the Figma 16px rhythm, so strip the theme margin.
  "& .MuiFormControl-root": {
    marginBottom: 0,
  },
  "& .MuiInputBase-root": {
    border: "1px solid #E8DFD7",
    borderRadius: "0.5rem",
    backgroundColor: "#FFFFFF",
    minHeight: "3rem",
  },
  // Figma has a single border per input. GvTextField paints a second gold
  // notchedOutline on focus (double border) — suppress it and the standard-
  // variant underline; the .MuiInputBase-root border above is the only one.
  "& .MuiInputBase-root .MuiOutlinedInput-notchedOutline": {
    border: "none !important",
  },
  "& .MuiInputBase-root::before, & .MuiInputBase-root::after": {
    display: "none",
  },
  "& .MuiInputBase-root .MuiInputBase-input": {
    fontSize: "0.875rem",
  },
  "& .MuiInputBase-root.Mui-focused": {
    borderColor: "#FEC20B",
  },
  "& .MuiInputBase-root.Mui-error": {
    borderColor: "#ED7857",
  },
  "& .MuiInputBase-root.Mui-disabled": {
    backgroundColor: "#EDEBEB",
  },
  "& .MuiInputBase-root.Mui-disabled .MuiInputBase-input": {
    WebkitTextFillColor: "#908E8D",
  },
  "& .MuiNativeSelect-select": {
    fontSize: "0.875rem",
  },
} as const;

const DOB_POPPER_PROPS = {
  placement: "bottom-start" as const,
  sx: {
    "& .MuiPickersDay-root.Mui-selected": {
      backgroundColor: "#FEC20B",
      color: "#262626",
    },
    "& .MuiPickersDay-root.Mui-focusVisible": {
      outline: "2px solid #FEC20B",
      outlineOffset: "1px",
    },
  },
} as const;

interface IFieldLabelProps {
  htmlFor: string;
  label: string;
  status?: FieldStatus;
}

const FieldLabel = ({ htmlFor, label, status }: IFieldLabelProps) => (
  // mb-[8px], not mb-2: this repo's tailwind maps spacing "2" to 2rem
  <label
    htmlFor={htmlFor}
    className="geograph-medium mb-[8px] flex w-full items-center text-xs leading-3 text-[#262626]"
  >
    {label}
    {status && <FieldStatusBadge status={status} />}
  </label>
);

export const PrequalCustomerInfoForm = ({
  pet,
  prefill,
  user,
  initialValues,
  serverErrors,
  onSubmit,
}: IPrequalCustomerInfoFormProps) => {
  const prefillValues = useMemo(() => buildPrefillValues(prefill, user), [prefill, user]);

  // Badge per field from the initial prefill snapshot only — badges must not
  // flip between "Missing" and "On file" while the user types.
  const fieldStatuses = useMemo(() => {
    const statusOf = (value: string): FieldStatus => (value ? "onFile" : "missing");
    return {
      firstName: statusOf(prefillValues.firstName),
      lastName: statusOf(prefillValues.lastName),
      email: statusOf(prefillValues.email),
      phone: statusOf(prefillValues.phone),
      street: statusOf(prefillValues.street),
      city: statusOf(prefillValues.city),
      state: statusOf(prefillValues.state),
      zip: statusOf(prefillValues.zip),
      dateOfBirth: "missing" as FieldStatus,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { control, handleSubmit, setError, clearErrors, watch, formState: { isValid } } =
    useForm<PrequalificationFormType>({
      mode: "onChange",
      resolver: yupResolver(PrequalificationSchema),
      defaultValues: initialValues ?
        buildSubmittedValues(initialValues) :
        { ...prefillValues, dateOfBirth: undefined, isAgreed: false },
    });

  const formFieldErrors = useMemo(() => getFormFieldErrors(serverErrors), [serverErrors]);

  const linkedServerErrorFields = useMemo(
    () => formFieldErrors
      .map(({ field }) => SERVER_ERROR_FIELD_MAP[field])
      .filter((group) => group.length > 1),
    [formFieldErrors],
  );

  useEffect(() => {
    let focused = false;
    formFieldErrors.forEach(({ field, messages }) => {
      // An on-file email is read-only, so an inline error there could never
      // be cleared; the summary above the fields still shows the message.
      const formFields = getEditableFormFields(field, {
        emailOnFile: fieldStatuses.email === "onFile",
      });
      formFields.forEach((formField, index) => {
        setError(
          formField,
          { type: "server", message: index === 0 ? messages.join(" ") : "" },
          { shouldFocus: !focused },
        );
        focused = true;
      });
    });
  }, [formFieldErrors, setError, fieldStatuses.email]);

  // Validation only re-checks the edited field, so clear the rest of a
  // linked group (e.g. ZIP + State) once the customer edits any of them.
  useEffect(() => {
    if (!linkedServerErrorFields.length) return undefined;
    const subscription = watch((_values, { name }) => {
      linkedServerErrorFields
        .filter((group) => group.includes(name as PrequalFormFieldName))
        .forEach((group) => clearErrors(group.filter((formField) => formField !== name)));
    });
    return () => subscription.unsubscribe();
  }, [linkedServerErrorFields, watch, clearErrors]);

  // "Consent viewed" = the customer actively opened a Scratch legal disclosure,
  // not merely rendering the consent block (same semantics as the tablet
  // kiosk). Ref-guarded to fire once per form even across multiple documents.
  const consentViewedRef = useRef(false);

  const handleLegalLinkClick = (disclosure: IScratchDisclosure) => {
    if (!consentViewedRef.current) {
      consentViewedRef.current = true;
      logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_CONSENT_VIEWED, {});
    }
    logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_LEGAL_LINK_OPENED, { disclosure: disclosure.key });
    openExternalLink(disclosure.url);
  };

  const submitForm = (values: PrequalificationFormType) => {
    onSubmit({
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone.replace(/\D/g, ""),
      street: values.street,
      city: values.city,
      state: values.state,
      zip: values.zip,
      dateOfBirth: format(values.dateOfBirth, "yyyy-MM-dd"),
      countryCode: user?.countryCode || undefined,
      consents: { sms: true, eca: true, privacy: true, creditPull: true },
      acknowledgedAt: new Date().toISOString(),
    });
  };

  const legalLink = (key: ScratchDisclosureKey) => {
    const disclosure = SCRATCH_DISCLOSURES.find((entry) => entry.key === key);
    if (!disclosure) return null;
    return (
      <strong
        className="cursor-pointer text-[#4157D8] underline"
        onClick={() => handleLegalLinkClick(disclosure)}
      >
        {disclosure.label}
      </strong>
    );
  };

  return (
    <div className="min-h-full w-full bg-[#F9F6F4]">
      <form
        onSubmit={handleSubmit(submitForm)}
        className="mx-auto w-full max-w-3xl px-4 py-6"
      >
        <div className="flex flex-col gap-5 rounded-xl border border-[#E8DFD7] bg-white p-6">
          <div className="flex items-start gap-4">
            <AvatarCustom image={pet?.image} name={pet?.name} width={72} height={72} />
            <div className="flex flex-col gap-[8px]">
              <Typography
                variant="h5"
                sx={{ fontFamily: "geograph-medium", fontSize: "1.25rem", lineHeight: "1.5rem" }}
              >
                {pet?.name}
              </Typography>
              <Typography variant="body1" sx={{ color: "#262626", fontSize: "1rem", lineHeight: "1.5rem" }}>
                {PREQUAL_COPY.FORM_SUBTITLE}
              </Typography>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Box sx={FIELD_SX}>
                <FieldLabel htmlFor="firstName" label="First Name" status={fieldStatuses.firstName} />
                <GvInputController control={control} name="firstName" placeholder="John" noLabel label="" />
              </Box>
              <Box sx={FIELD_SX}>
                <FieldLabel htmlFor="lastName" label="Last Name" status={fieldStatuses.lastName} />
                <GvInputController control={control} name="lastName" placeholder="Edwards" noLabel label="" />
              </Box>
            </div>

            <Box sx={FIELD_SX}>
              <FieldLabel htmlFor="email" label="Your Email" status={fieldStatuses.email} />
              <GvInputController
                control={control}
                name="email"
                placeholder="Enter your email"
                disabled={fieldStatuses.email === "onFile"}
                noLabel
                label=""
              />
            </Box>

            <Box sx={FIELD_SX}>
              <FieldLabel htmlFor="phone" label="Mobile Phone" status={fieldStatuses.phone} />
              <GvInputMaskController
                sx={MASKED_FIELD_SX}
                control={control}
                name="phone"
                label=""
                mask="999-999-9999"
                placeholder="XXX-XXX-XXXX"
                errorVariant="alert"
              />
            </Box>

            <Box sx={FIELD_SX}>
              <FieldLabel htmlFor="street" label="Street Address" status={fieldStatuses.street} />
              <GvInputController control={control} name="street" placeholder="Enter Street Address" noLabel label="" />
            </Box>

            <Box sx={FIELD_SX}>
              <FieldLabel htmlFor="city" label="City" status={fieldStatuses.city} />
              <GvInputController control={control} name="city" placeholder="Enter City" noLabel label="" />
            </Box>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Box sx={FIELD_SX}>
                <FieldLabel htmlFor="state" label="State" status={fieldStatuses.state} />
                <PrequalStateSelect
                  control={control}
                  name="state"
                  placeholder="Select State"
                  options={US_STATES}
                />
              </Box>
              <Box sx={FIELD_SX}>
                <FieldLabel htmlFor="zip" label="ZIP" status={fieldStatuses.zip} />
                <GvInputController
                  control={control}
                  name="zip"
                  placeholder="Enter ZIP"
                  inputProps={{ maxLength: 10 }}
                  noLabel
                  label=""
                />
              </Box>
            </div>

            <Box sx={FIELD_SX}>
              <FieldLabel htmlFor="dateOfBirth" label="Date of Birth" status={fieldStatuses.dateOfBirth} />
              <GvDatePickerController
                control={control}
                name="dateOfBirth"
                label=""
                disableFuture
                popperProps={DOB_POPPER_PROPS}
              />
            </Box>
          </div>

          <p className="!text-wrap text-sm">
            <GvCheckboxController
              control={control}
              name="isAgreed"
              alignType="start"
              checkboxClassName="!p-0 !pe-1 !pt-0.5"
              labelClassName="!ml-0"
              fullWidth={false}
              label={
                <span className="text-sm text-[#262626]">
              By checking on the box and clicking &quot;Check Eligibility&quot;, I authorize
              GoodVets to collect and share my information with Scratch Financial, Inc.
              (&quot;Scratch&quot;), and I authorize Scratch and its service providers to obtain
              and use consumer report information about me from one or more consumer reporting
              agencies, to verify my identity and determine my eligibility for a monthly or
              annual subscription plan to GoodVets&apos; wellness program services. More
              information about how Scratch collects and uses consumer report information is
              available in Scratch&apos;s {legalLink("CREDIT_PULL_AUTHORIZATION")}. This
              eligibility check is a soft credit inquiry and will not affect my credit score.
              Eligibility does not guarantee final terms or availability and may be subject to
              verification and other eligibility criteria. I also agree to
              Scratch&apos;s {legalLink("ELECTRONIC_COMMUNICATIONS")} and{" "}
                  {legalLink("SMS_CONSENT")}, and acknowledge
                Scratch&apos;s {legalLink("PRIVACY_POLICY")}.
                </span>
              }
            />
          </p>
        </div>

        <div className="mx-auto mt-6 w-full max-w-xs">
          <GvPrimaryButton type="submit" isDisabled={!isValid}>
            {PREQUAL_COPY.CTA}
          </GvPrimaryButton>
        </div>
      </form>
    </div>
  );
};

export default PrequalCustomerInfoForm;
